import { type ReactElement, useContext } from "react"

import { useTranslation } from "react-i18next"

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import { Button, Typography } from "@synapse-analytics/synapse-ui"
import { useFormikContext } from "formik"

import { Context } from "../../Context"
import { currentThresholdMetricsData } from "../../Helpers"
import type { ConfigFormikProps, ConfigMode } from "../../Interfaces"

import styles from "./Header.module.scss"

/**
 * A header component for the AutoML configuration page.
 * It displays the configuration version number and offers a button to create a new configuration or save the current one.
 * @param {{ mode: ConfigMode }} props - Whether the component is being used to display a history view or not.
 * @returns {ReactElement} The header component.
 */
export function Header(props: Readonly<{ mode: ConfigMode }>): ReactElement {
  const { mode } = props
  const { t } = useTranslation("ModelConfigurations")

  const {
    thresholdMetricsData,
    selectedVersionNum,
    latestVersionNum,
    isSidebarOpen,
    setIsSidebarOpen,
    setCurrentConfigHistory,
    setCurrentThresholdMetricData,
  } = useContext(Context)

  const {
    values: { labels },
    initialValues: { positiveThreshold, negativeThreshold, singleThreshold },
    isSubmitting,
    setFieldValue,
    submitForm,
  } = useFormikContext<ConfigFormikProps>()

  const newConfigurationBtnClickHandler = (): void => {
    let initialThresholdMetricsData

    setFieldValue("positiveLabel", { name: labels[0] })
    setFieldValue("negativeLabel", { name: labels[1] })

    if (thresholdMetricsData?.type === "V2") {
      setFieldValue("negativeThreshold", negativeThreshold)
      setFieldValue("positiveThreshold", positiveThreshold)

      initialThresholdMetricsData = currentThresholdMetricsData({
        thresholdMetrics: thresholdMetricsData.thresholdMetrics,
        positiveValue: positiveThreshold,
        negativeValue: negativeThreshold,
      })
    } else if (thresholdMetricsData) {
      setFieldValue("singleThreshold", singleThreshold)

      initialThresholdMetricsData = currentThresholdMetricsData({
        thresholdMetrics: thresholdMetricsData.thresholdMetrics,
        singleSliderValue: singleThreshold,
      })
    }

    setCurrentThresholdMetricData(initialThresholdMetricsData)

    setCurrentConfigHistory(undefined)
  }

  return (
    <header className={styles.configHeaderContainer}>
      <Typography variant="h3-bold">
        {mode === "view" ? t("header.titles.configuration") : t("header.titles.newConfiguration")}
      </Typography>
      <Typography variant="label-regular" variantColor={2}>
        v{mode === "view" ? (selectedVersionNum || 0).toFixed(1) : ((latestVersionNum || 0) + 1).toFixed(1)}
      </Typography>

      <div>
        {mode === "view" ? (
          <Button
            onClick={newConfigurationBtnClickHandler}
            variant="primary"
            startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
            size="regular"
          >
            {t("header.buttons.newConfiguration")}
          </Button>
        ) : (
          <Button
            variant="secondary"
            role="button"
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={submitForm}
          >
            {t("header.buttons.configure")}
          </Button>
        )}
        {!isSidebarOpen && (
          <Button variant="secondary" onClick={setIsSidebarOpen.bind(null, (prev) => !prev)}>
            {t("header.buttons.history")}
          </Button>
        )}
      </div>
    </header>
  )
}
